import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../FirebaseAppContext';
import { HybridIcon } from '../components/HybridIcon';
import type { Customer } from '../models';
import './Customers.css';

function getCustomerContact(customer: Customer): string {
  const email = customer.email?.trim();
  const phone = customer.phone?.trim();
  if (email && phone) {
    return `${email} | ${phone}`;
  }
  return customer.contactInfo || email || phone || 'N/A';
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

export default function Customers() {
  const { state } = useAppContext();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const customers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return state.customers;
    }

    return state.customers.filter((customer) => {
      return (
        (customer.name || '').toLowerCase().includes(q) ||
        (customer.email || '').toLowerCase().includes(q) ||
        (customer.phone || '').toLowerCase().includes(q) ||
        (customer.contactInfo || '').toLowerCase().includes(q) ||
        (customer.driverLicense || '').toLowerCase().includes(q)
      );
    });
  }, [state.customers, query]);

  const stats = useMemo(() => {
    const withEmail = customers.filter((customer) => Boolean(customer.email)).length;
    const withPhone = customers.filter((customer) => Boolean(customer.phone)).length;

    return {
      total: customers.length,
      withEmail,
      withPhone,
    };
  }, [customers]);

  return (
    <div className="customers-page">
      <section className="customers-hero card">
        <div>
          <p className="eyebrow">Customer Relations</p>
          <h1 className="customers-title">Customers</h1>
          <p className="customers-subtitle">Manage your client directory, contact channels, and license details in one clean list.</p>
        </div>

        <button className="btn btn-primary customers-add" onClick={() => navigate('/customers/new')}>
          <HybridIcon name="add-circle" size={18} color="currentColor" />
          Add Customer
        </button>
      </section>

      <section className="customers-metrics">
        <article className="metric">
          <span>Total customers</span>
          <strong>{stats.total}</strong>
        </article>
        <article className="metric">
          <span>With email</span>
          <strong>{stats.withEmail}</strong>
        </article>
        <article className="metric">
          <span>With phone</span>
          <strong>{stats.withPhone}</strong>
        </article>
      </section>

      <section className="customers-toolbar card">
        <HybridIcon name="search" size={18} color="var(--ink-3)" />
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by name, email, phone, or license"
          className="customers-search-input"
        />
        {query.length > 0 && (
          <button className="btn btn-secondary customers-clear" onClick={() => setQuery('')} type="button">
            Clear
          </button>
        )}
      </section>

      <section className="customers-list">
        {customers.length === 0 ? (
          <div className="card customers-empty">
            <HybridIcon name="people-outline" size={44} color="var(--ink-3)" />
            <h3>No customers found</h3>
            <p>Try a different search term or add a new customer.</p>
          </div>
        ) : (
          customers.map((customer) => (
            <button
              key={customer.id}
              className="lrow customers-row"
              onClick={() => navigate(`/customers/${customer.id}`)}
              type="button"
            >
              <div className="avatar customers-avatar">{getInitials(customer.name || 'NA')}</div>
              <div className="customers-main">
                <div className="customers-line1">
                  <strong>{customer.name}</strong>
                  <span className="pill pill-accent">License {customer.driverLicense || 'N/A'}</span>
                </div>
                <div className="customers-line2">{getCustomerContact(customer)}</div>
              </div>
              <HybridIcon name="chevron-forward" size={18} color="var(--ink-3)" />
            </button>
          ))
        )}
      </section>
    </div>
  );
}
